import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { mortgageMiniCalculatorApi } from '../api';
import { TType as TPageType } from '../../common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/GetOffersV1Request';
import { setRedirectUrl, setFinancePlatformEnabled, setProgramsVersion } from '../redux';
import {
  selectEntityId,
  selectFormDefaults,
  selectPageType,
  selectRealEstateType,
  selectRegionId,
  selectSkipOffersFetch,
} from '../selectors/calculator';
import { IGetOffersV1Response } from '../../common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/GetOffersV1Response';
import { TObjectType, TRealEstateType } from '../types';
import { IMetaV2 } from 'shared/common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/MetaV2';
import { IMeta } from 'shared/common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/Meta';

// Изменить тип когда будем выпиливать v1
interface IParams {
  id: number;
  regionId?: number;
  objectType?: TObjectType | string;
  realEstate?: { id?: number; type?: TRealEstateType };
  type: TPageType;
  objectCost: number;
  downPayment: number;
  loanTermYears: number;
}

export const hasProgramsVersion = (meta: IMetaV2 | IMeta): meta is IMetaV2 => {
  return 'programsVersion' in meta;
};

export type IFetchOffersProps = Omit<IParams, 'id' | 'type' | 'objectType' | 'realEstate' | 'regionId'>;

export const useGetOffers = () => {
  const dispatch = useDispatch();
  const { objectCost, downPayment, loanTermYears, benefitProgram, objectType } = useSelector(selectFormDefaults);
  const entityId = useSelector(selectEntityId);
  const pageType = useSelector(selectPageType);
  const regionId = useSelector(selectRegionId);
  const realEstateType = useSelector(selectRealEstateType);
  const skipOffersFetch = useSelector(selectSkipOffersFetch);

  // TODO: убрать ненужные значения https://jira.cian.tech/browse/CD-236273
  const initialParams = {
    id: entityId,
    type: pageType,
    regionId,
    objectType,
    objectCost,
    downPayment,
    loanTermYears,
    benefitProgram,
    realEstate: realEstateType && { id: entityId, type: realEstateType },
  };
  const [params, setParams] = useState<IParams>(initialParams);

  const { data, isFetching, isError, error, refetch } = mortgageMiniCalculatorApi.endpoints.getOffers.useQuery(params, {
    skip: skipOffersFetch,
  });

  const fetchOffers = (newParams: IFetchOffersProps) => {
    setParams(prevParams => ({ ...prevParams, ...newParams }));
  };

  // Обработка данных
  if (data) {
    const { generalLink, financePlatformEnabled } = data.meta;

    dispatch(setRedirectUrl({ url: generalLink }));
    dispatch(setFinancePlatformEnabled(financePlatformEnabled));

    if (hasProgramsVersion(data.meta)) {
      dispatch(setProgramsVersion(data.meta.programsVersion));
    }
  }

  return {
    data: isError ? null : (data as IGetOffersV1Response),
    isFetching,
    error: isError ? error : null,
    refetch,
    fetchOffers,
  };
};
